/**
 * Product Category Chart - Following proper chart patterns
 * Uses withChartConfiguration HOC for consistency
 * Follows developer agent and data engineer principles
 */

import React, { useMemo, useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { UnifiedChartFactory } from '../services/UnifiedChartFactory';
import { formatMonetaryValue } from '../utils/chartUtils';
import { categoryDataService, CategoryApiResponse, CategoryData } from '../services/CategoryDataService';
import { Download, FileSpreadsheet, Pencil } from 'lucide-react';
import { dataValidationService } from '../services/DataValidationService';
import { ChartSizingUtils } from '../utils/ChartSizingUtils';
import { useServiceData } from '../hooks/useChartData';
import { useDataFetchError } from '../hooks/useChartError';
import { FilenameUtils } from '../utils/filenameUtils';
import { TitleUtils } from '../utils/titleUtils';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { ChartMetadata } from './ChartMetadata';
import '../styles/chart-components.css';

interface ProductCategoryChartProps extends ChartProps {
  timeframe: string;
  weekOffset: number;
  yearVisibility?: {
    show2023: boolean;
    show2024: boolean;
    show2025: boolean;
  };
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}

// Props for the wrapper component (data is fetched internally)
type ProductCategoryChartWrapperProps = Omit<ProductCategoryChartProps, 'data'>;

/**
 * Configuration function for chart options
 */
const getProductCategoryChartOption = (props: ProductCategoryChartProps & EnhancedChartProps) => {
  const { data, yearVisibility, timeframe, weekOffset, filters } = props;

  if (!data?.categories?.length) {
    return null;
  }

  const categories = data.categories as CategoryData[];

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'category',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  // Update title and subtitle based on year visibility
  let dynamicTitle = data.title || 'Top Product Categories by Funding';
  let dynamicSubtitle = data.subtitle || '';

  // Add year visibility info to subtitle if not all years are visible
  if (yearVisibility) {
    const visibleYears = [];
    if (yearVisibility.show2023) visibleYears.push('2023');
    if (yearVisibility.show2024) visibleYears.push('2024');
    if (yearVisibility.show2025) visibleYears.push('2025');

    if (visibleYears.length < 3 && visibleYears.length > 0) {
      const yearText = visibleYears.length === 1
        ? visibleYears[0]
        : `${visibleYears.slice(0, -1).join(', ')} & ${visibleYears.slice(-1)}`;
      dynamicSubtitle = `${data.subtitle || ''} • Showing ${yearText} data`;
    }
  }

  // Use custom title if exists
  const finalTitle = customTitle?.title || dynamicTitle;
  const finalSubtitle = customTitle?.subtitle || dynamicSubtitle;

  // Use UnifiedChartFactory for consistent styling
  const option = UnifiedChartFactory.createProductCategoryChart(
    categories,
    finalTitle,
    finalSubtitle,
    {
      timeframe: timeframe as '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom',
      weekOffset: weekOffset,
      chartContext: 'default'
    }
  );

  // Enhance tooltip with custom formatting
  option.tooltip = {
    ...option.tooltip,
    formatter: (params: any) => {
      const param = params[0];
      const index = param.dataIndex;
      const category = categories[index];

      return `
        <div style="font-weight: 600; font-size: 18px; margin-bottom: 8px; color: #633FF3;">
          ${category.name}
        </div>
        <div style="font-size: 16px;">
          <div style="margin: 4px 0;">
            <span style="font-weight: 500;">Funding:</span>
            <span style="font-weight: 700; color: #633FF3;">${formatMonetaryValue(category.amount)}</span>
          </div>
          <div style="margin: 4px 0;">
            <span style="font-weight: 500;">Share:</span>
            <span style="font-weight: 600;">${category.percentage}%</span>
          </div>
          <div style="margin: 4px 0;">
            <span style="font-weight: 500;">Deals:</span>
            <span style="font-weight: 600;">${category.count}</span>
          </div>
          ${category.companies ? `
            <div style="margin: 4px 0;">
              <span style="font-weight: 500;">Companies:</span>
              <span style="font-weight: 600;">${category.companies}</span>
            </div>
          ` : ''}
        </div>
      `;
    }
  };

  return option;
};

/**
 * Base chart component that receives data and renders
 */
const ProductCategoryChartBase: React.FC<ProductCategoryChartProps & EnhancedChartProps> = React.memo(({
  data,
  timeframe,
  weekOffset,
  yearVisibility,
  chartRef,
  isLoading,
  error,
  downloadChart,
  refreshChart,
  filters,
  metadata,
  ...restProps
}) => {
  // Title editor state - ALL HOOKS MUST BE AT THE TOP
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(() => ({
    chartType: 'category',
    timeframe,
    filters
  }), [timeframe, filters]);

  // Check for custom title - use useMemo to re-compute when forceRefresh changes
  const customTitle = React.useMemo(() =>
    customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // Refresh chart when forceRefresh changes (after state update completes)
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Get chart option for title extraction (must be before conditional returns)
  const chartOption = React.useMemo(() =>
    getProductCategoryChartOption({ data, timeframe, weekOffset, yearVisibility, filters } as ProductCategoryChartProps & EnhancedChartProps),
    [data, timeframe, weekOffset, yearVisibility, filters, forceRefresh]
  );
  const titleOption = Array.isArray(chartOption?.title) ? chartOption.title[0] : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  // Validate data before processing
  const validation = dataValidationService.validateCategoryData(data);
  if (!validation.isValid) {
    console.warn('Category data validation failed:', validation.errors);
  }

  const hasData = data?.categories && data.categories.length > 0;

  if (error) {
    return (
      <div className="chart-container-error">
        <div className="chart-error">
          <p className="chart-error-title">Error loading category data</p>
          <p className="chart-error-message">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!hasData && !isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3 className="chart-title">
            Top Product Categories by Funding
          </h3>
        </div>
        <div className="chart-empty">
          <p className="chart-empty-message">
            No category data available for this timeframe
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="chart-container-loading">
        <div className="chart-loading">
          <div className="chart-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Calculate height based on number of categories (should be 10 max)
  // Account for multi-line labels by giving more space per category
  const categoryCount = Math.min(data?.categories?.length || 0, 10);
  const chartHeight = UnifiedChartFactory.calculateCategoryChartHeight(categoryCount);

  const handleCSVExport = () => {
    if (!data?.categories) return;

    // Generate CSV content
    const headers = ['Category', 'Funding Amount', 'Deal Count', 'Share %'];
    const rows = data.categories.map((cat: CategoryData) => [
      cat.name,
      cat.amount,
      cat.count,
      cat.percentage
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `product-categories-funding-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate title and subtitle using TitleUtils
  const topCategory = data?.categories?.[0]?.name;
  const topCategoryShare = data?.categories?.[0]?.percentage;
  const dataPoints = data?.categories?.length || 0;

  const titleContext = {
    timeframe: timeframe,
    weekOffset: weekOffset,
    yearVisibility: yearVisibility,
    topCategory,
    topCategoryShare,
    dataPoints,
    isRealtime: true
  };

  const title = TitleUtils.generateProductCategoryTitle(titleContext);
  const subtitle = TitleUtils.generateProductCategorySubtitle(titleContext);

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas - title is IN the chart, not here */}
        <div
          ref={chartRef}
          className="w-full"
          style={{
            height: '800px', // Increased height for better spacing
            minHeight: '700px',
            maxHeight: '1200px'
          }}
        />

        {/* Chart Metadata */}
        {metadata && (
          <ChartMetadata
            periodStart={metadata.current_period?.start}
            periodEnd={metadata.current_period?.end}
            cacheStatus={metadata.cache_status}
            responseTimeMs={metadata.response_time_ms}
          />
        )}
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1); // useEffect will call refreshChart()
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1); // useEffect will call refreshChart()
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'category',
          timeframe,
          topCategory: topCategory
        }}
      />
    </>
  );
});

/**
 * Data fetching wrapper component using unified patterns
 */
const ProductCategoryChartWithData: React.FC<ProductCategoryChartWrapperProps> = ({
  timeframe,
  weekOffset,
  yearVisibility,
  filters,
  metadata
}) => {
  const { data, isLoading, error } = useServiceData(
    () => categoryDataService.getCategoryData(timeframe, weekOffset, filters),
    [timeframe, weekOffset, filters],
    {
      validationEnabled: true,
      retryOnError: true,
      maxRetries: 3
    }
  );

  // Extract metadata from API response (data includes cache_status and response_time_ms)
  const chartMetadata = data ? {
    current_period: data.metadata?.current_period || { start: '', end: '' },
    cache_status: data.cache_status,
    response_time_ms: data.response_time_ms
  } : undefined;

  return (
    <ProductCategoryChartWithHOC
      data={data}
      timeframe={timeframe}
      weekOffset={weekOffset}
      yearVisibility={yearVisibility}
      filters={filters}
      metadata={chartMetadata}
      isLoading={isLoading}
      error={error}
    />
  );
};

// Create the HOC-wrapped component
const ProductCategoryChartWithHOC = withChartConfiguration(
  ProductCategoryChartBase,
  getProductCategoryChartOption
);

export default ProductCategoryChartWithData;
